/**
 * Authentication State Store (Zustand)
 * Keeps tokens in memory to reduce XSS persistence risk.
 */

import { create } from 'zustand';
import type { User } from '../types/auth';

interface AuthState {
  // State
  token: string | null;
  user: User | null;
  sessionStatus: 'checking' | 'signed-in' | 'signed-out';
  sessionCheckError: string | null;
  isAuthenticated: boolean;
  isOwner: boolean;
  actualOrgId: number | null;
  actingOrgId: number | null;
  actingOrgName: string | null;
  isImpersonating: boolean;

  // Actions
  setAuth: (token: string, user: User) => void;
  rotateSession: (token: string, user: User) => void;
  setUser: (user: User) => void;
  clearAuth: () => void;
  beginSessionCheck: () => void;
  setSessionCheckError: (message: string | null) => void;
  setActingOrg: (orgId: number | null, orgName?: string | null) => void;
}

const resolveIsOwner = (user: User | null) => Boolean(user?.is_platform_owner);

const clearLegacyAuthStorage = () => {
  if (typeof window === 'undefined') {
    return;
  }
  [
    'access_token',
    'refresh_token',
    'user',
    'actual_org_id',
    'acting_org_id',
    'acting_org_name',
    'is_impersonating',
  ].forEach((key) => localStorage.removeItem(key));
};

clearLegacyAuthStorage();

export const useAuthStore = create<AuthState>((set) => ({
  // Initial state
  token: null,
  user: null,
  sessionStatus: 'checking',
  sessionCheckError: null,
  isAuthenticated: false,
  isOwner: false,
  actualOrgId: null,
  actingOrgId: null,
  actingOrgName: null,
  isImpersonating: false,

  // Set authentication (after successful login)
  setAuth: (token: string, user: User) => {
    const actingOrgId =
      user.organization_id ??
      user.impersonated_organization_id ??
      user.actual_organization_id ??
      null;
    const actualOrgId = user.actual_organization_id ?? user.organization_id ?? null;
    const isImpersonating =
      Boolean(user.impersonating) ||
      (actualOrgId !== null && actingOrgId !== null && actualOrgId !== actingOrgId);
    const actingOrgName =
      user.impersonated_organization_name ??
      (isImpersonating ? user.organization_name ?? null : null);

    const isOwner = resolveIsOwner(user);
    set({
      token,
      user,
      sessionStatus: 'signed-in',
      sessionCheckError: null,
      isAuthenticated: true,
      isOwner,
      actualOrgId,
      actingOrgId,
      actingOrgName,
      isImpersonating,
    });
  },

  // Update user information only
  setUser: (user: User) => {
    const actualOrgId = user.actual_organization_id ?? user.organization_id ?? null;
    const actingOrgId =
      user.organization_id ?? user.impersonated_organization_id ?? actualOrgId;
    const isImpersonating =
      Boolean(user.impersonating) ||
      (actualOrgId !== null && actingOrgId !== null && actualOrgId !== actingOrgId);
    const actingOrgName =
      user.impersonated_organization_name ??
      (isImpersonating ? user.organization_name ?? null : null);

    const isOwner = resolveIsOwner(user);
    set({
      user,
      isOwner,
      actualOrgId,
      actingOrgId,
      actingOrgName,
      isImpersonating,
    });
  },

  // Clear authentication (logout)
  clearAuth: () => {
    clearLegacyAuthStorage();
    set({
      token: null,
      user: null,
      sessionStatus: 'signed-out',
      sessionCheckError: null,
      isAuthenticated: false,
      isOwner: false,
      actualOrgId: null,
      actingOrgId: null,
      actingOrgName: null,
      isImpersonating: false,
    });
  },

  beginSessionCheck: () =>
    set({
      token: null,
      user: null,
      sessionStatus: 'checking',
      sessionCheckError: null,
      isAuthenticated: false,
      isOwner: false,
      actualOrgId: null,
      actingOrgId: null,
      actingOrgName: null,
      isImpersonating: false,
    }),

  // Rotate credentials while preserving the user's selected tenant context.
  rotateSession: (token: string, user: User) =>
    set({
      token,
      user,
      isAuthenticated: true,
      sessionStatus: 'signed-in',
      sessionCheckError: null,
      isOwner: resolveIsOwner(user),
    }),

  setSessionCheckError: (message: string | null) =>
    set({ sessionCheckError: message }),

  setActingOrg: (orgId: number | null, orgName?: string | null) =>
    set((state) => {
      const actualOrg =
        state.actualOrgId ??
        state.user?.actual_organization_id ??
        state.user?.organization_id ??
        null;
      const nextOrgId = orgId ?? actualOrg;
      const impersonating =
        actualOrg !== null && nextOrgId !== null && actualOrg !== nextOrgId;
      const targetName =
        nextOrgId === null ? null : (orgName ?? state.actingOrgName ?? null);

      if (
        state.actingOrgId === nextOrgId &&
        state.actingOrgName === targetName &&
        state.isImpersonating === impersonating
      ) {
        return state;
      }

      const updatedUser = state.user
        ? {
            ...state.user,
            organization_id: nextOrgId ?? state.user.organization_id ?? null,
            impersonated_organization_id: impersonating ? nextOrgId : null,
            impersonated_organization_name: impersonating ? targetName : null,
            impersonating,
          }
        : state.user;

      return {
        actingOrgId: nextOrgId,
        actingOrgName: targetName,
        isImpersonating: impersonating,
        user: updatedUser,
      };
    }),
}));

// Selector hooks for convenience
export const useAuth = () => useAuthStore((state) => ({
  token: state.token,
  user: state.user,
  isAuthenticated: state.isAuthenticated,
  isOwner: state.isOwner,
}));

export const useUser = () => useAuthStore((state) => state.user);

export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);

export const useIsOwner = () => useAuthStore((state) => state.isOwner);

export const useActualOrgId = () => useAuthStore((state) => state.actualOrgId);
export const useActingOrgId = () => useAuthStore((state) => state.actingOrgId);
export const useActingOrgName = () => useAuthStore((state) => state.actingOrgName);
export const useIsImpersonating = () => useAuthStore((state) => state.isImpersonating);
export const useSetActingOrg = () => useAuthStore((state) => state.setActingOrg);
